import classNames from 'classnames';
import {
  format,
  isAfter,
  isBefore,
  isPast,
  isSameDay,
  startOfDay,
  startOfHour,
  startOfMonth,
  subDays,
  subMonths,
} from 'date-fns';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import type { ChevronProps, DayButtonProps } from 'react-day-picker';
import type {
  MenuListProps,
  OptionProps,
  SingleValueProps,
} from 'react-select';
import { components } from 'react-select';
import InsightsRestriction from '../admin/insights/InsightsRestriction';
import InsightsTimeOption from '../admin/insights/InsightsTimeOption';
import useAppSelector from '../app/redux/useAppSelector';
import type { PlanTier } from '../graphql/generated/graphql';
import { UsageAnalyticsDays } from '../organization/PricingConfig';
import useIsLayoutSize from '../post/utils/useIsLayoutSize';
import { LayoutSize } from '../post/utils/useLayoutSize';
import ButtonSelect from './ButtonSelect';
import Caret from './Caret';
import * as styles from './DateRangePicker.module.scss';
import DayButton from './DayButton';
import DayPicker from './DayPicker';
import FloatingCard from './FloatingCard';
import HoverView from './HoverView';
import Popover from './Popover';
import Select from './Select';

type Dates = {
  startAt: Date | null;
  endAt: Date | null;
};

type TimeRange = {
  label: string;
  value: number | 'custom' | Dates;
};

type TimeRanges = readonly { options: readonly TimeRange[] }[];

const DEFAULT_TIME_RANGES = [
  {
    options: [
      { label: '7 days', value: 7 },
      { label: '14 days', value: 14 },
      { label: '30 days', value: 30 },
      { label: '90 days', value: 90 },
    ],
  },
  {
    options: [{ label: 'Custom', value: 'custom' }],
  },
] as const satisfies TimeRanges;

type DatesMode = 'daily' | 'hourly';

const getNewDates = ({
  startDate = null,
  endDate = null,
  defaultStartAt,
  defaultEndAt,
  mode,
}: {
  startDate?: Date | null;
  endDate?: Date | null;
  defaultStartAt?: never;
  defaultEndAt?: never;
  mode: DatesMode;
}): Dates => {
  const startOfDate = mode === 'daily' ? startOfDay : startOfHour;
  const newStartAt = startDate ? startOfDate(startDate) : defaultStartAt;

  let newEndAt = endDate ? startOfDate(endDate) : defaultEndAt;

  if (startDate && newStartAt && newEndAt && newStartAt > newEndAt) {
    newEndAt = startOfDate(startDate);
  }

  return {
    startAt: newStartAt ?? null,
    endAt: newEndAt ?? null,
  };
};

const getDatesForNumberOfDays = (days: number, mode: DatesMode) =>
  getNewDates({
    startDate: subDays(new Date(), days),
    endDate: mode === 'daily' ? subDays(new Date(), 1) : new Date(),
    mode,
  });

export const DEFAULT_TIME_RANGE = DEFAULT_TIME_RANGES[0].options[2].value;
export const DEFAULT_DATES = getDatesForNumberOfDays(
  DEFAULT_TIME_RANGE,
  'daily',
) as {
  startAt: Date;
  endAt: Date;
};

const Chevron = (props: ChevronProps) => (
  <Caret direction={props.orientation} block size={10} />
);

interface DateRangePickerProps {
  ranges?: TimeRanges;
  onDatesChange: (
    dates: {
      startAt: moment.Moment;
      endAt: moment.Moment;
    },
    value: TimeRange['value'],
  ) => void;
  firstAllowedDate?: moment.Moment;
  initialRange?: TimeRange['value'];
  variant?: 'button' | 'input';
  mode?: DatesMode;
  maxHeight?: number;
  initialStage?: 'startDate' | 'endDate' | null;
}

const DateRangePicker = ({
  ranges = DEFAULT_TIME_RANGES,
  onDatesChange,
  firstAllowedDate,
  initialRange = DEFAULT_TIME_RANGE,
  variant = 'input',
  mode = 'daily',
  maxHeight,
  initialStage = null,
}: DateRangePickerProps) => {
  const SelectComponent = variant === 'button' ? ButtonSelect : Select;

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [timeRange, setTimeRange] = useState<TimeRange['value']>(initialRange);

  const [dates, setDates] = useState<Dates>(
    getDatesForNumberOfDays(
      typeof timeRange === 'number' ? timeRange : 7,
      mode,
    ),
  );

  const [stage, setStage] = useState(initialStage);
  const isDayPickerOpen = !!stage;

  const isLarge = useIsLayoutSize(LayoutSize.desktop);

  const handleChangeDates = (newDates: Dates, value: TimeRange['value']) => {
    setDates(newDates);
    if (newDates.startAt && newDates.endAt) {
      onDatesChange(
        {
          startAt: moment(newDates.startAt),
          endAt: moment(newDates.endAt),
        },
        value,
      );
    }
  };

  const handleTimeRangeChange = (value: TimeRange['value'] | null) => {
    if (!value) {
      return;
    }
    if (typeof value === 'number') {
      handleChangeDates(getDatesForNumberOfDays(value, mode), value);
    } else if (value === 'custom') {
      setStage('startDate');
    } else {
      handleChangeDates(value, value);
    }
    setTimeRange(value);
  };

  const defaultValue = useMemo(
    () =>
      ranges
        .flatMap((r) => r.options)
        .find((range) => range.value === initialRange),
    [ranges, initialRange],
  );

  const datesString = `${
    dates.startAt ? format(dates.startAt, 'MMM d') : ''
  } - ${dates.endAt ? format(dates.endAt, 'MMM d') : ''}`;

  const range = {
    from: dates.startAt ?? undefined,
    to: dates.endAt ?? undefined,
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  const RangePickerDayButton = (props: DayButtonProps) => {
    const planTier = useAppSelector(
      (state) => state.session.organization.planTier,
    ) as PlanTier;

    if (props.modifiers.hidden) {
      // react-day-picker requires an empty fragment
      // eslint-disable-next-line react/jsx-no-useless-fragment
      return <></>;
    }

    const inner = <DayButton {...props} />;

    if (props.disabled && isPast(props.day.date)) {
      return (
        <HoverView
          placement="top"
          hoverDelay="fast"
          hoverGroupId="dateRangePicker"
          view={
            <FloatingCard>
              <InsightsRestriction
                onUpgradeClick={() => {
                  setStage(null);
                }}
                olderThan={UsageAnalyticsDays[planTier]}
              />
            </FloatingCard>
          }
        >
          <div>{inner}</div>
        </HoverView>
      );
    }
    return inner;
  };

  return (
    <div>
      <Popover
        placement="bottom-end"
        onOpenChange={() => isDayPickerOpen && setStage(null)}
        open={isDayPickerOpen}
        view={
          timeRange === 'custom' && (
            <FloatingCard noArrow>
              <div
                className={classNames(styles.dayPickerWrapper, {
                  [styles.large]: isLarge,
                })}
              >
                <DayPicker
                  mode="range"
                  numberOfMonths={isLarge ? 2 : 1}
                  selected={range}
                  modifiers={{
                    first: range.from ?? false,
                    last: range.to ?? range.from ?? false,
                  }}
                  disabled={{
                    after: new Date(),
                    before: firstAllowedDate?.toDate(),
                  }}
                  onSelect={(newRange) => {
                    const changedDate =
                      newRange?.from &&
                      range.from &&
                      isSameDay(newRange.from, range.from)
                        ? (newRange.to ?? null)
                        : (newRange?.from ?? null);

                    if (!changedDate) {
                      return;
                    }

                    if (stage === 'startDate') {
                      handleChangeDates(
                        getNewDates({
                          startDate: changedDate,
                          endDate:
                            (range.from && isBefore(changedDate, range.from)) ||
                            (range.to && isAfter(changedDate, range.to))
                              ? null
                              : range.to,
                          mode,
                        }),
                        timeRange,
                      );

                      setStage('endDate');
                      return;
                    }
                    if (range.from && isBefore(changedDate, range.from)) {
                      handleChangeDates(
                        getNewDates({
                          startDate: changedDate,
                          endDate: null,
                          mode,
                        }),
                        timeRange,
                      );
                      return;
                    }
                    handleChangeDates(
                      getNewDates({
                        startDate: range?.from ?? null,
                        endDate: newRange?.to ?? changedDate ?? null,
                        mode,
                      }),
                      timeRange,
                    );
                    setTimeout(() => {
                      setStage(null);
                    }, 100);
                  }}
                  defaultMonth={
                    isLarge
                      ? subMonths(startOfMonth(new Date()), 1)
                      : new Date()
                  }
                  components={{ DayButton: RangePickerDayButton, Chevron }}
                />
              </div>
            </FloatingCard>
          )
        }
      >
        <div>
          <SelectComponent<TimeRange, false>
            className={classNames(styles.select, styles[variant])}
            isClearable={false}
            defaultValue={defaultValue}
            onChange={handleTimeRangeChange}
            menuIsOpen={isMenuOpen}
            options={ranges}
            onMenuOpen={() => {
              setStage(null);
              setIsMenuOpen(true);
            }}
            onMenuClose={() => setIsMenuOpen(false)}
            isSearchable={false}
            value={timeRange}
            isOptionSelected={(option) => {
              if (typeof option.value === 'string') {
                return timeRange === 'custom';
              }
              if (typeof option.value === 'number') {
                return timeRange === option.value;
              }

              return (
                typeof timeRange !== 'string' &&
                typeof timeRange !== 'number' &&
                moment(timeRange.startAt).isSame(option.value.startAt) &&
                moment(timeRange.endAt).isSame(option.value.endAt)
              );
            }}
            components={{
              Option: (props: OptionProps<TimeRange, false>) => (
                <InsightsTimeOption
                  {...props}
                  onUpgradeClick={() => setIsMenuOpen(false)}
                />
              ),
              MenuList: (props: MenuListProps<TimeRange, false>) => (
                <components.MenuList
                  {...props}
                  maxHeight={maxHeight ?? props.maxHeight}
                >
                  <div className={styles.titleContainer}>
                    <div className={styles.title}>
                      {typeof timeRange === 'number'
                        ? `Last ${timeRange} days`
                        : props.getValue()[0]?.label}
                    </div>
                    <div className={styles.titleDates}>{datesString}</div>
                  </div>
                  {props.children}
                </components.MenuList>
              ),
              GroupHeading: () => <hr className={styles.groupHeading} />,
              SingleValue: (props: SingleValueProps<TimeRange, false>) => (
                <components.SingleValue {...props}>
                  {typeof timeRange === 'string' ? datesString : props.children}
                </components.SingleValue>
              ),
            }}
          />
        </div>
      </Popover>
    </div>
  );
};

export default DateRangePicker;
